import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, KeyRound, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BookingButton from "@/components/BookingButton";

const PASS_KEY = "sano_circle_pass";

export default function CirclePage() {
  const [pass, setPass] = useState(() => localStorage.getItem(PASS_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const verify = async (p) => {
    setLoading(true);
    try {
      const { data } = await api.post("/circle/verify", { password: p });
      localStorage.setItem(PASS_KEY, p);
      setContent(data.content);
      setAuthed(true);
    } catch {
      toast.error("That's not the current code. Check the most recent member email.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (pass) verify(pass); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copyCode = async (code) => {
    if (!code || code === "—") return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success(`Copied: ${code}`);
      setTimeout(() => setCopied(null), 1500);
    } catch (err) { console.error("Copy failed:", err); /* silent */ }
  };

  if (!authed) {
    return (
      <main className="min-h-[85vh] flex items-center justify-center px-6" data-testid="circle-login-page">
        <form
          onSubmit={(e) => { e.preventDefault(); if (pass) verify(pass); }}
          className="w-full max-w-md p-10 border"
          style={{ background: "var(--sano-bg)", borderColor: "var(--sano-border)" }}
          data-testid="circle-login-form"
        >
          <div className="flex items-center gap-2 eyebrow mb-4">
            <Lock size={14} strokeWidth={1.5} /> Sano Circle · Members
          </div>
          <h1 className="font-serif-display text-4xl leading-tight mb-2">A quiet room.</h1>
          <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--sano-ink-2)" }}>
            For members of the Sano Circle. Enter the monthly access code from your
            welcome email or workshop ticket.
          </p>
          <input
            type="password"
            placeholder="Access code"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm mb-4"
            style={{ borderColor: "var(--sano-border)" }}
            data-testid="circle-pass-input"
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !pass}
            className="w-full px-7 py-3.5 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
            style={{ background: "var(--sano-olive)" }}
            data-testid="circle-pass-submit"
          >
            {loading ? "Checking…" : "Enter"}
          </button>

          <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--sano-border)" }}>
            <div className="eyebrow mb-2">Not a member yet?</div>
            <p className="text-xs leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
              The Sano Circle opens to anyone who attends a workshop or books a
              first session. You&apos;ll receive the current month&apos;s code via email.
            </p>
            <div className="mt-4">
              <BookingButton testid="circle-join-cta" size="sm" label="Book a session to join" />
            </div>
          </div>

          <div className="mt-6 text-center">
            <Link to="/" className="text-xs border-b border-current pb-0.5" data-testid="circle-back-home">
              ← Back to site
            </Link>
          </div>
        </form>
      </main>
    );
  }

  if (!content) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="circle-loading">
        <div className="eyebrow">Loading…</div>
      </main>
    );
  }

  const c = content;

  return (
    <main className="min-h-[85vh]" data-testid="circle-page">
      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 eyebrow mb-3">
            <KeyRound size={14} strokeWidth={1.5} /> Sano Circle · Members
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl leading-[0.95] mb-4">
            {c.seasonTitle}
          </h1>
          <p className="text-lg leading-relaxed max-w-2xl" style={{ color: "var(--sano-ink-2)" }}>
            {c.intro}
          </p>

          {/* Ritual of the month */}
          <section className="mt-16" data-testid="circle-ritual">
            <div className="eyebrow mb-3">Ritual of the month</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl mb-6">{c.ritualOfTheMonth.title}</h2>
            <ol className="space-y-3 list-decimal pl-6">
              {c.ritualOfTheMonth.steps.map((s, i) => (
                <li key={i} className="text-base leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>{s}</li>
              ))}
            </ol>
          </section>

          {/* Recipes */}
          <section className="mt-20" data-testid="circle-recipes">
            <div className="eyebrow mb-3">Recipes for this season</div>
            <div className="grid md:grid-cols-3 gap-px" style={{ background: "var(--sano-border)" }}>
              {c.recipes.map((r, i) => (
                <article key={i} className="p-8" style={{ background: "var(--sano-bg)" }} data-testid={`circle-recipe-${i}`}>
                  <h3 className="font-serif-display text-2xl mb-2">{r.title}</h3>
                  <p className="text-sm italic mb-4" style={{ color: "var(--sano-ink-2)" }}>{r.blurb}</p>
                  <div className="eyebrow mb-2">Ingredients</div>
                  <ul className="text-sm mb-4 space-y-1 list-disc pl-5" style={{ color: "var(--sano-ink-2)" }}>
                    {r.ingredients.map((ing, j) => <li key={j}>{ing}</li>)}
                  </ul>
                  <div className="eyebrow mb-2">Method</div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>{r.method}</p>
                </article>
              ))}
            </div>
          </section>

          {/* Member perks */}
          <section className="mt-20" data-testid="circle-perks">
            <div className="eyebrow mb-3">Your member perks</div>
            <div className="space-y-3">
              {c.member_perks.map((p, i) => (
                <div
                  key={i}
                  className="p-6 border grid sm:grid-cols-12 gap-4 items-center"
                  style={{ background: "var(--sano-bg)", borderColor: "var(--sano-border)" }}
                  data-testid={`circle-perk-${i}`}
                >
                  <div className="sm:col-span-4">
                    <div className="font-serif-display text-xl">{p.title}</div>
                  </div>
                  <div className="sm:col-span-5 text-sm" style={{ color: "var(--sano-ink-2)" }}>
                    {p.detail}
                  </div>
                  <div className="sm:col-span-3 sm:text-right">
                    <button
                      onClick={() => copyCode(p.code)}
                      disabled={!p.code || p.code === "—"}
                      className="inline-flex items-center gap-2 px-4 py-2 border rounded-full text-xs font-mono disabled:opacity-40"
                      style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg-2)" }}
                      data-testid={`circle-copy-${i}`}
                    >
                      {copied === p.code ? <CheckCircle2 size={12} strokeWidth={1.5} /> : <Copy size={12} strokeWidth={1.5} />}
                      {p.code}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Acupressure protocol */}
          <section className="mt-20" data-testid="circle-protocol">
            <div className="eyebrow mb-3">{c.acupressureProtocol.title}</div>
            <div className="grid sm:grid-cols-3 gap-px" style={{ background: "var(--sano-border)" }}>
              {c.acupressureProtocol.points.map((pt, i) => (
                <div key={i} className="p-6" style={{ background: "var(--sano-bg)" }}>
                  <div className="font-serif-display text-xl mb-2">{pt.n}</div>
                  <div className="text-sm leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>{pt.how}</div>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-20 flex flex-wrap gap-4">
            <BookingButton testid="circle-book" />
            <Link to="/" className="inline-flex items-center text-sm border-b border-current pb-0.5" data-testid="circle-home-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
